import { useAtom } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { logger } from "c7-logger";

import { useButtonDimensions } from "chameleon/lib/theme/helpers";
import { theme } from "chameleon/lib/theme/theme.css";

import { paymentSummaryAtom } from "checkout-experience/atoms/payment-atoms";
import { displayMonetaryValue } from "checkout-experience/components/common/monetary-util";
import isoConverter from "checkout-experience/components/payment-method/pay-options/apple/utils/iso-converter";
import { usePaymentOptionContext } from "checkout-experience/components/payment-method/payment-option-provider";
import { useChexContext } from "checkout-experience/providers/chex-context-wrapper";
import { getSupportedCardTypesListAsAppleStrings } from "checkout-experience/utils/payment-helpers";
import { AppleBundle, PaymentDomain } from "checkout-query-client/api/types";
import { useApplePayClientSession } from "checkout-query-client/hooks/use-apple-pay-session";
import { ChexFieldValues } from "checkout-query-client/types/chex-form-fields";
import { PaymentType } from "checkout-query-client/types/form-types";

import { CardNetwork, PayResponse_Status } from "frontend-client";

interface ApplePayButtonProps {
  paymentDomain?: PaymentDomain;
}

export const ApplePayButton = ({ paymentDomain }: ApplePayButtonProps) => {
  const { trigger, watch } = useFormContext();
  const { paymentOption } = usePaymentOptionContext() ?? {};
  const [applePayBrowserSession, setApplePayBrowserSession] =
    useState<ApplePaySession | null>(null);
  const [validationURL, setValidationUrl] = useState("");
  const [paymentSummary, setPaymentSummary] = useAtom(paymentSummaryAtom);
  const {
    merchantSettings,
    checkoutSession,
    checkoutSessionToken = "",
    apiHost = "",
    version = "",
    isDemo,
    iframePayDomain,
  } = useChexContext();
  const targetDomain: string = iframePayDomain
    ? iframePayDomain
    : window.location.hostname;
  const applePayClientSession = useApplePayClientSession(
    checkoutSessionToken,
    apiHost,
    version,
    validationURL,
    targetDomain
  );
  const { orderSummary } = checkoutSession ?? {};
  const { height, padding } = useButtonDimensions(theme);

  const applePayButtonRef = useRef<HTMLInputElement | null>(null);

  const { i18n } = useTranslation();

  const getApplePayRequest = () => {
    const subtotalAmountStr = displayMonetaryValue(
      orderSummary?.subtotalAmount
    ).replace(/[^0-9.]/g, ""); // TODO: Make this internationalized.  Apple's docs are unclear about some inputs.  This is out of scope of initial task.

    const totalShippingCostStr = displayMonetaryValue(
      orderSummary?.totalShippingCost
    ).replace(/[^0-9.]/g, ""); // TODO: Make this internationalized.  Apple's docs are unclear about some inputs.  This is out of scope of initial task.

    const totalTaxStr = displayMonetaryValue(orderSummary?.totalTax).replace(
      /[^0-9.]/g,
      ""
    ); // TODO: Make this internationalized.  Apple's docs are unclear about some inputs.  This is out of scope of initial task.

    const totalAmountStr = displayMonetaryValue(
      orderSummary?.totalAmount
    ).replace(/[^0-9.]/g, ""); // TODO: Make this internationalized.  Apple's docs are unclear about some inputs.  This is out of scope of initial task.

    const acceptedCards: Array<CardNetwork> = (
      merchantSettings?.paymentMethods?.cardPaymentMethod?.supportedCards ?? []
    ).map((m: { network: CardNetwork }) => m.network);

    // ISO 3166 to ISO 4217
    const currencyCode = isoConverter[merchantSettings!.country];

    const capabilitiesList = () => {
      const options: string[] = ["supports3DS"];
      // if conditions will be added here for future regions
      return options;
    };
    return {
      // ISO-3166
      countryCode: merchantSettings!.country,
      // ISO 4217
      currencyCode,
      supportedNetworks: getSupportedCardTypesListAsAppleStrings(acceptedCards),
      merchantCapabilities: capabilitiesList(),
      lineItems: [
        {
          label: "SUBTOTAL",
          type: "final",
          amount: subtotalAmountStr,
        },
        {
          label: "SHIPPING",
          type: "final",
          amount: totalShippingCostStr,
        },
        {
          label: "ESTIMATED TAX",
          type: "final",
          amount: totalTaxStr,
        },
      ],
      total: {
        label: merchantSettings?.merchantDisplayName,
        amount: totalAmountStr,
      },
      requiredBillingContactFields: ["postalAddress", "name"],
    };
  };

  const handlePostBundle = useCallback(
    async (appleBundle?: AppleBundle) => {
      if (!checkoutSessionToken || !appleBundle || !paymentDomain) {
        return PayResponse_Status.FAILURE;
      }

      const formFields = watch(`${paymentOption?.formId}`) as ChexFieldValues;

      return paymentDomain({
        appleBundle,
        paymentType: PaymentType.applePay,
        submitType: PaymentType.applePay,
        formFields,
        paymentSummary,
      });
    },
    [checkoutSessionToken, paymentDomain]
  );

  const initApplePay = () => {
    const request = getApplePayRequest();
    const session = new ApplePaySession(
      14, // latest Apple Device supported version
      request as ApplePayJS.ApplePayPaymentRequest
    );

    // Used by the apple auth flow.  This fires after the user authorizes the payment
    // eslint-disable-next-line
    session.onpaymentauthorized = async ({
      payment,
    }: ApplePayJS.ApplePayPaymentAuthorizedEvent) => {
      const {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        token: { paymentData, paymentMethod },
        billingContact,
      } = payment;

      setPaymentSummary?.({
        paymentType: PaymentType.applePay,
        last4: paymentMethod.displayName.toUpperCase(),
      });

      if (billingContact) {
        const status = await handlePostBundle({
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          paymentData,
          billingContact,
          paymentMethod,
        });
        if (status === PayResponse_Status.SUCCESS) {
          session.completePayment({ status: 0 });
        } else {
          session.completePayment({ status: 1 });
        }
      }
    };

    session.onvalidatemerchant = (
      e: ApplePayJS.ApplePayValidateMerchantEvent
    ) => {
      if (e.validationURL.length) {
        setValidationUrl(e.validationURL);
      } else {
        logger.error("Apple SDK did not return a valid URL");
      }
    };

    session.oncancel = () => {
      setValidationUrl("");
    };

    setApplePayBrowserSession(session);
    session.begin();
  };

  const handleOnClick = () => {
    trigger(`${paymentOption?.formId}`)
      .then((isValid) => {
        if (isValid) {
          if (isDemo) {
            paymentDomain?.({
              paymentType: PaymentType.applePay,
              submitType: PaymentType.applePay,
            }).catch(logger.error);
            return;
          }
          initApplePay();
        }
      })
      .catch((err) => logger.error(err));
  };

  const openPaySheet = (paymentSession: string) => {
    try {
      applePayBrowserSession!.completeMerchantValidation(
        JSON.parse(paymentSession)
      );
    } catch (e) {
      logger.error(
        "An Error occurred when Apple SDK attempted completeMerchantValidation.",
        JSON.stringify(e)
      );
    }
  };

  useEffect(() => {
    const { isSuccess, data: fulfilled, isError } = applePayClientSession;
    if (isSuccess) {
      openPaySheet(fulfilled.paymentSession);
    } else if (isError) {
      logger.error(
        "An Error occurred when fetching the  Client Session from Checkout API"
      );
    }
  }, [applePayClientSession.isSuccess, applePayBrowserSession]);

  useEffect(() => {
    if (!applePayButtonRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      if (applePayButtonRef?.current) {
        applePayButtonRef.current.onclick = handleOnClick; // attach our JPMC onClick handler

        // overrides for rendered 'apple-pay-button' component
        const applePayButton = document.querySelector(
          "apple-pay-button"
        ) as HTMLBaseElement;
        applePayButton.style.width = "100%";
        applePayButton.style.minWidth = "200px";
        applePayButton.style.borderRadius = theme.borderRadius.button;

        // overrides for nested .apple-pay-btn - (note that observer is necessary since SDK component renders this)
        const applePayBtn = applePayButton?.shadowRoot?.querySelector(
          ".apple-pay-btn"
        ) as HTMLElement;
        applePayBtn?.setAttribute("style", `height:${height + padding * 2}px`);
      }
    });
    resizeObserver.observe(applePayButtonRef.current);
    return () => resizeObserver.disconnect(); // clean up
  }, []);

  return (
    <span style={{ width: "100%" }} data-testid="apple-pay-button-span">
      <apple-pay-button
        buttonstyle={
          merchantSettings?.paymentMethods?.walletPaymentMethod?.applePay
            ?.applePayButton?.buttonStyle || "black"
        }
        type={
          merchantSettings?.paymentMethods?.walletPaymentMethod?.applePay
            ?.applePayButton?.type || "plain"
        }
        locale={i18n?.resolvedLanguage || ""}
        ref={applePayButtonRef}
      ></apple-pay-button>
    </span>
  );
};
